import React from 'react';
import PropTypes from 'prop-types';
import { Link, Routes, Route, NavLink, Navigate, useParams, useLocation } from 'react-router-dom';

// Third-party packages.
import Helmet from 'react-helmet';

// Types.
import InstanceTarget from '../../types/instance/InstanceTarget';
import EventType from '../../types/instance/EventType';
import HostTarget from '../../types/host/HostTarget';

// Components.
import PeriodBreadcrumb from '../../component/PeriodBreadcrumb';
import Breadcrumb from "../../container/breadcrumb";
import HeaderActions from "../../container/header_actions";
import TabbedMenu from "../../container/tabbed-menu";
import TextAndPlan from "./tabs/TextAndPlan";
import ConditionalRender from "../../helpers/ConditionalRender";
import FilterBar from "../../component/FilterBar";
import GenericActivity from "./tabs/GenericActivity";
import Events from "./tabs/Events";

interface IStatementDetailProps {
    instances: InstanceTarget[],
    hosts: HostTarget[],
    eventTypes: EventType[],
    toggleMenu: Function,
    applyPeriod: Function,
    setFilterOptions: Function
}


const GenericStatement = (props: IStatementDetailProps) => {
    const { instances } = props;
    const { instanceId, batchId, statementId, batchStatementId } = useParams();
    const getInstance: InstanceTarget[] = instances.filter(instance => instance.id === Number(instanceId))
    const instance = getInstance[0]
    const location = useLocation();
    // const [chartPlanPlotLines, setChartPlanPlotLines] = useState<ChartPlotLine[]>([]);

    if (instance === undefined) {
        return (
            <React.Fragment>No Instance</React.Fragment>
        )
    }

    const getURL = (newSegment: string) => {
        const segments = location.pathname.split('/');
        segments[segments.length - 1] = newSegment;
        return segments.join('/');
    };

    return (
        <div id="content">
            <Helmet>
                <title>{instance.name} - Statement View - DBmarlin</title>
                <meta name="description"
                      content="Instance statement performance view, showing key performance metrics and detailed executed statement and waits information"/>
            </Helmet>

            {/* Heading */}
            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb instanceId={instance.id}
                            type="instances"
                            heading={instance.name}>
                    <Link to="/instances">Analysis</Link>
                    <Link to="/instances">Instances</Link>
                    <Link to={`/instances/${instanceId}/activity`}>{instance.name}</Link>
                    <Link to={`/instances/${instanceId}/activity`}>Activity</Link>
                    <NavLink to={`/hosts/`}
                             className="current-breadcrumb-link pe-none">{statementId || batchId}</NavLink>
                    <PeriodBreadcrumb/>
                </Breadcrumb>

                <HeaderActions toggleMenu={props.toggleMenu} refresh={() => {
                }}
                               applyPeriod={props.applyPeriod}/>
            </div>


            <div className="loader"/>
            <TabbedMenu>
                {/*<TimeShift/>*/}

                <NavLink to={getURL('text')}
                         className={({ isActive }) => (isActive ? 'active' : '')}>
                    SQL Text and Plans
                </NavLink>
                <NavLink to={getURL('activity')}
                          className={({ isActive }) => (isActive ? 'active' : '')}>
                    SQL Performance
                </NavLink>


                <NavLink to={getURL('events')} className={({ isActive }) => (isActive ? 'active' : '')}>
                    Events
                </NavLink>
            </TabbedMenu>

            {/* Filters */}
            <ConditionalRender if={props.setFilterOptions}>
                {props.setFilterOptions &&
                    <FilterBar setFilterOptions={props.setFilterOptions}/>}
            </ConditionalRender>

            <Routes>
                <Route
                    path={"/text"}
                    element={<TextAndPlan
                        setExecutionPlanOptions={() => {}}
                        batchId={batchId}
                        batchStatementId={batchStatementId}
                        instance={instance}
                        statementId={statementId}
                    />}
                />

                <Route
                    path={"/activity"}
                    element={<GenericActivity
                        instanceId={instance.id}
                        instanceType={instance.type}
                        applyPeriod={props.applyPeriod}
                        // @ts-ignore
                        statementId={statementId}
                        apiString='sqlhash'
                    />}
                />

                <Route
                        path='/events'
                        element={
                            <Events
                                    instance={instance}
                                    applyPeriod={props.applyPeriod}
                                    batchStatementId={batchStatementId}
                                    statementId={statementId || "0"}
                            />
                        }
                />

                <Route path="*" element={<Navigate to="/not-found" />} />

            </Routes>
        </div>
    )
}

GenericStatement.propTypes = {
    toggleMenu: PropTypes.func.isRequired,
    applyPeriod: PropTypes.func.isRequired,
    setFilterOptions: PropTypes.func.isRequired
};

export default GenericStatement;